// =============================================================================
// pageDirty — the app-wide "there is unsaved work on this page" registry.
//
// <Modal> has had a dirty guard since W4.2 (useDirtyClose): a stray backdrop
// tap can never discard typed work. Leaving a PAGE had no equivalent. Three
// pages hand-rolled their own (ExporterInvoices, ExporterShipmentDetail,
// FarmSettings) and everywhere else the back arrow, the bottom nav and the
// desktop rail discarded silently. Gate Intake was the worst of them: a whole
// delivery form plus two photos, gone on one mis-tap while a truck waits at the
// weighbridge.
//
// react-router's own answer (useBlocker) is not open to us — it requires a data
// router and the app mounts <BrowserRouter> (main.tsx). So this is a registry
// the shared navigation controls consult at CLICK time rather than a router
// concern. Click-time is deliberate: nothing re-renders when a page becomes
// dirty, so arming the guard costs the capture path nothing.
//
// ⚠ The invariant that matters most here is the NEGATIVE one: NO PROMPT WHEN
// NOTHING WAS TYPED. A confirm that fires on an untouched form teaches
// operators to hit "Discard" without reading it, and then the real warning —
// the one in front of forty minutes of work — gets tapped through too. So
// pageIsDirty() answers false unless a page has explicitly ARMED a baseline
// AND the live snapshot differs from it. usePageDirtyGuard owns those arming
// rules for typed work; the only other entry is usePageLeaveGuard, for a page
// whose unsaved thing is a live recording (a GPS walk), not a form — it is
// "dirty" exactly while the recording runs, and brings its own question.
// =============================================================================

import { confirmDialog } from '@/components/Dialogs'

/** The question asked on the way out. The default is about TYPED work; a page
 *  guarding something else — a GPS walk that is recording — names that
 *  instead, because "Discard unsaved changes?" is the wrong question to put to
 *  a scout whose track is kept and offered back (farm audit SC-07). */
export interface LeavePrompt {
  title:   string
  message: string
  confirm: string
  cancel:  string
}

const DEFAULT_PROMPT: LeavePrompt = {
  title:   'Discard unsaved changes?',
  message: 'You have entered information on this page that has not been saved yet. Leaving now loses it.',
  confirm: 'Discard & leave',
  cancel:  'Keep editing',
}

/** What counts as LEAVING for a guard, beyond "the page unmounts".
 *  `samePathStays`: a link to the same pathname with any other query does not
 *  leave. True for a live recording (Scouting's walk): the walk lives in the
 *  mounted page, which a query change never unmounts, so asking "Stop the
 *  walk?" there promises a stop that never happens — the Farm Map's
 *  `?block=` deep link and every useUrlState filter put a query on the URL, and
 *  the Scouting tab (bare path) then asked (M-01 fix round 2). Left false for
 *  typed work, whose editor may live in the query (`?invoice=new`). */
export interface LeaveScope { samePathStays?: boolean }

type Guard = { isDirty: () => boolean; prompt?: LeavePrompt; scope?: LeaveScope }

/** id → "is this page dirty right now?". Read at click time, never rendered. */
const guards = new Map<string, Guard>()

/** Register a page's dirty predicate (and, optionally, the question to ask and
 *  what counts as leaving). Returns the deregister for useEffect. */
export function registerPageDirty(id: string, isDirty: () => boolean, prompt?: LeavePrompt, scope?: LeaveScope): () => void {
  guards.set(id, { isDirty, prompt, scope })
  return () => { guards.delete(id) }
}

function firstDirty(): Guard | undefined {
  for (const g of guards.values()) if (g.isDirty()) return g
  return undefined
}

/** Does any mounted page hold unsaved typed work? */
export function pageIsDirty(): boolean {
  return firstDirty() !== undefined
}

/**
 * Ask before leaving. Resolves true when it is safe to navigate.
 *
 * Returns true with NO dialog when nothing is dirty — callers can therefore
 * wrap every navigation unconditionally. Callers on a hot path should still
 * take the synchronous `pageIsDirty()` fast path first, so a clean page's
 * navigation is not deferred by a microtask (see BackButton).
 */
export async function confirmLeavePage(): Promise<boolean> {
  const dirty = firstDirty()
  if (!dirty) return true
  return confirmDialog({ ...(dirty.prompt ?? DEFAULT_PROMPT), tone: 'danger' })
}

/** The click-time half every guarded LINK shares (GuardedNavLink, GuardedLink).
 *  Leaves the click alone — the link navigates as it always did — when the
 *  page is clean, when the click is modified (a new tab leaves the page
 *  standing), when an own onClick already cancelled it, and when the link
 *  points at the very URL on screen: following it does not unmount the page,
 *  so "Leave and stop?" would promise something that never happens (the
 *  active Scouting tab tapped mid-walk asked, and the walk recorded on).
 *  Otherwise it cancels the click and calls `go` once the operator agrees.
 *
 *  `target` and `here` are `pathname + search`. By default the search counts:
 *  a page that keeps its editor in the query (`?invoice=new`) is torn down by a
 *  link to its bare path, so that is a real exit and still asks. A guard that
 *  registered `samePathStays` (a recording) compares the pathname only — see
 *  LeaveScope. With several dirty guards, the link asks when ANY of them is
 *  left, with that guard's question. */
export function guardLeaveClick(
  e: { defaultPrevented: boolean; button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; preventDefault: () => void },
  target: string,
  here: string,
  go: () => void,
): void {
  if (e.defaultPrevented) return
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  if (!wouldLeave(target, here)) return
  e.preventDefault()
  void confirmLeaveTo(target, here).then(ok => { if (ok) go() })
}

/** Would going to `target` from `here` leave a page that holds unsaved work?
 *  The rule guardLeaveClick applies to a link, for a caller that navigates in
 *  code — the ⌘K palette's Go to. False when nothing is dirty, when `target` is
 *  the URL on screen, and for a recording guard (`samePathStays`) when only the
 *  query differs: none of those unmount the page, so asking "Stop the walk?"
 *  there promises a stop that never happens (⌘K → Scouting on Scouting mid-walk
 *  asked, and "Leave and stop" left the walk recording — M-01 fix round 3). */
export function wouldLeave(target: string, here: string): boolean {
  return firstDirtyLeftBy(target, here) !== undefined
}

/** confirmLeavePage for a known destination: asks the question of the first
 *  dirty guard that going to `target` would leave, and resolves true with NO
 *  dialog when it leaves none (see wouldLeave). */
export async function confirmLeaveTo(target: string, here: string): Promise<boolean> {
  const left = firstDirtyLeftBy(target, here)
  if (!left) return true
  return confirmDialog({ ...(left.prompt ?? DEFAULT_PROMPT), tone: 'danger' })
}

const pathOf = (url: string) => url.split(/[?#]/, 1)[0]

/** The first dirty guard that following `target` from `here` would leave. */
function firstDirtyLeftBy(target: string, here: string): Guard | undefined {
  if (target === here) return undefined
  const samePath = pathOf(target) === pathOf(here)
  for (const g of guards.values()) {
    if (samePath && g.scope?.samePathStays) continue
    if (g.isDirty()) return g
  }
  return undefined
}

/** Test seam — the registry is module state, so suites must clear it. */
export function __resetPageDirty(): void { guards.clear() }
